/**
 * **Why a section will not appear** — C24 item 11,
 * [ADR-0049](../../../../docs/adr/0049-a-section-with-content-is-drawn-and-the-builder-says-when-it-is-not.md) §2.
 *
 * The owner, 2026-10-01:
 *
 * > *in some pages componants/section able to add but it not get visible in
 * > preview. if we have template section and it have valid input it should be
 * > visible in website*
 *
 * Every renderer reads its props into a view model and returns `null` when it
 * does not like what it finds, and `draw` draws nothing for `null`. The builder
 * meanwhile showed the section, showed its filled fields, and said nothing. A
 * firm added a section, filled it in, looked at the preview, and the section
 * was not there.
 *
 * ## One expression, two readers
 *
 * This asks the renderer's own question — `blocks/index.tsx: blockView`, which
 * *is* the switch `renderBlock` dispatches on — and turns its answer into words.
 * So the builder's notice and the page's behaviour are the same expression and
 * cannot drift: a renderer that started refusing for a new reason would be
 * reported by this without anybody remembering to come here.
 *
 * `tests/site-visibility.test.ts` holds that to every one of the block types.
 *
 * ## Two reasons, and they are not the same kind of thing
 *
 * - **`empty`** — there is nothing to draw. The firm can finish it, and the
 *   notice says what is missing.
 * - **`policy`** — the firm's profession forbids it (ADR-0014). The firm must
 *   *not* finish it, and the notice says which switch and where it lives.
 *
 * ADR-0022 §4's finding is why this is the right shape: the ICSI's and Rule
 * 36's restraint is **presentational**, not a refusal, because a theme that
 * refused a section would put compliance in the gallery. The builder saying
 * *why* is the same principle one screen further in.
 *
 * ## What this cannot know, and will not guess
 *
 * **Seven sections are filled in by the service at render**, not by the firm:
 * `services`, `blog_list`, `product_spotlight`, `pricing`, `price_list`,
 * `hours` and `contact` read the firm's catalog, its posts, its working hours
 * or its contact details and have those written **into the block's props**
 * before the payload leaves (`app/domain/site.py: _resolve_block`). The
 * builder holds only what the firm typed.
 *
 * So for those, this answers only where the *typed* part is decisive — a
 * `pricing` section with no services chosen will not draw whatever the catalog
 * holds — and otherwise returns `null`.
 *
 * **It declines rather than guesses, and the direction matters.** The harmful
 * error is telling a firm a section will not appear when it will: the firm
 * deletes it, or writes the same thing twice. Saying nothing about a section
 * that then does not appear is the defect we started from, which is worse for
 * the other seventy-odd percent of sections and is fixed for them. A caller
 * that *does* hold the facts passes them (`SiteFacts`) and gets the full
 * answer.
 *
 * ## What this is not
 *
 * It is **not** a validator and it never refuses a save. A section with nothing
 * in it is a section a firm is part-way through writing, and a builder that
 * would not let it be saved would be a builder that cannot be used.
 */

import { blockView } from './index'
import type { BlockContext, ContactDetails } from './common'

/**
 * A firm with no contact details, for a caller that has none — which is the
 * builder. `contact` is the one thing a section may read from the site rather
 * than from its own props, so the honest answer for a `contact` section in the
 * builder is the answer for a firm that has filled nothing in.
 */
const NO_CONTACT: ContactDetails = {
  email: null,
  phone: null,
  whatsapp: null,
  address: null,
  hours: null,
  map_url: null,
}

/** Why a section is not drawn, in the firm's own words. */
export interface HiddenReason {
  /** `empty`: finishable. `policy`: must not be finished. */
  code: 'empty' | 'policy'
  /** One sentence, addressed to the firm. */
  message: string
  /** For a `policy` reason: the conduct switch, and where it is changed. */
  settingsHref?: string
  switchName?: string
}

/**
 * **Which conduct switch suppresses which section** (ADR-0014, ADR-0022 §6).
 *
 * The service's `SUPPRESSED_BY_SWITCH`, and it is the authority — it drops
 * these sections from the payload before the renderer ever sees them, and the
 * readers check again so this half can *test* the rule. Served on
 * `vocabulary.policy.suppressed_by`; the builder reads that and this is the
 * copy the renderer's half needs, held to the fixture by
 * `tests/site-vocabulary.test.ts`.
 */
export const SUPPRESSED_BY: Record<string, string> = {
  testimonial_strip: 'show_testimonials',
  logo_strip: 'show_client_names',
  story_strip: 'show_client_names',
}

/** Where a firm changes its conduct switches (C13, ADR-0032 §1). */
export const CONDUCT_HREF = '/site/policy'

/**
 * **The sections the service fills in at render** — `_resolve_block`'s seven.
 * Their content is the firm's catalog, its posts, its hours or its contact
 * details, written into the props before the payload leaves. The builder holds
 * only what the firm typed, so this is the set where a verdict needs
 * `SiteFacts` or is declined.
 */
export const RESOLVED_AT_RENDER = new Set([
  'services',
  'blog_list',
  'product_spotlight',
  'pricing',
  'price_list',
  'hours',
  'contact',
  // `lead_form` too, and it was missed on the first pass: the service
  // resolves it (`_resolve_block`), and whether it draws depends on
  // `payload.forms.enquiry` — a form that posts nowhere is not drawn (FR-I6).
  // The builder does not hold that, so judging it from props alone reported
  // every filled lead form as invisible. Caught by the no-false-alarm test.
  'lead_form',
])

/**
 * What a caller knows about the firm, beyond the block's own props. Every
 * field is optional and an absent one means *do not judge the sections that
 * need it* — never *there is none*.
 */
export interface SiteFacts {
  /** The firm's contact details, as the render payload carries them. */
  contact?: ContactDetails
  /** How many services the firm's catalog holds. */
  services?: number
  /** How many published posts the firm has. */
  posts?: number
  /** Whether *Your firm* carries any working hours. */
  hasHours?: boolean
}

/**
 * **"Choose services" with none ticked** — C27, ADR-0058 §3 (UAT report #8).
 *
 * A section that offers *All services* stores how its services were chosen
 * beside the slugs (`service_pick`; `contact` names it `form_service_pick`).
 * `chosen` with nothing ticked draws **nothing** — the service's
 * `chosen_services` — so for `services`, whose whole content is that list,
 * the typed part is decisive and this can say so without the catalogue.
 * Absent means `all` (§1), which is never this.
 *
 * Exported because the picker says the same thing under the field, in the
 * same words, and two sentences for one fact would drift.
 */
export const NO_SERVICES_CHOSEN =
  'No services chosen — this section won’t be shown on your site. Tick at least one, or choose All services.'

/** Whether a section's props say *chosen, and none ticked* (ADR-0058 §3). */
export function choseNoServices(props: Record<string, unknown>, slugsKey = 'service_slugs'): boolean {
  const pickKey = slugsKey.replace(/slugs$/, 'pick')
  if (props[pickKey] !== 'chosen') return false
  const slugs = props[slugsKey]
  return !(Array.isArray(slugs) && slugs.length > 0)
}

/** What the firm must have chosen for a catalog-backed section to draw. */
const NEEDS_SLUGS: Record<string, string> = {
  product_spotlight: 'at least one service chosen',
  pricing: 'at least one service chosen',
}

/** The switch's words, for the notice. */
const SWITCH_WORDS: Record<string, string> = {
  show_testimonials: 'show testimonials',
  show_client_names: 'name your clients',
  show_prices_public: 'show prices on your website',
}

/** What each type needs before it draws, said in the firm's words. */
const NEEDS: Record<string, string> = {
  hero: 'a heading',
  about: 'a heading, or something to say',
  services: 'at least one service',
  team: 'at least one person',
  faq: 'at least one question',
  blog_list: 'a heading',
  page_hero: 'a heading or a subheading',
  showcase: 'a heading',
  metrics: 'at least one figure with a value',
  split_feature: 'something to say, a point, or a picture',
  product_spotlight: 'a heading',
  feature_grid: 'at least one item with a title',
  logo_strip: 'at least one client name',
  testimonial_strip: 'at least one quote',
  statement: 'a line to say',
  steps: 'at least one step with a title',
  story_strip: 'at least one story',
  lead_form: 'a heading',
  cta: 'a headline',
  countdown: 'a date and time to count to',
  schedule: 'at least one day with something on it',
  tickets: 'at least one ticket with a name',
  pricing: 'at least one plan with a name',
  tabs: 'at least one tab with a label',
  timeline: 'at least one entry',
  video: 'a link to the video',
  before_after: 'at least one pair of pictures',
  marquee: 'at least one word or phrase',
  comparison: 'a column, and a row with a label',
  hours: 'opening and closing times on at least one day',
  price_list: 'at least one group with an item',
  courses: 'at least one course with a title',
  openings: 'at least one role with a title',
  goal: 'a target to raise',
  product_grid: 'at least one product with a name and a picture',
  rich_text: 'some words',
  image: 'a picture and its description',
}

/** The two with no view model — read here, because `blockView` has no case. */
function rawIsEmpty(type: string, props: Record<string, unknown>): boolean {
  if (type === 'image') {
    // `image_url`, which is the schema's name — `url` was a guess and it made
    // every filled image report itself invisible.
    const url = props.image_url
    return typeof url !== 'string' || url.trim() === ''
  }
  // `rich_text` holds a document; empty is no node with any text in it.
  const doc = props.body ?? props.content ?? props.document
  return !hasText(doc)
}

/** Whether a `rich_text` document carries a single character of text. */
function hasText(node: unknown): boolean {
  if (typeof node === 'string') return node.trim() !== ''
  if (Array.isArray(node)) return node.some(hasText)
  if (node && typeof node === 'object') {
    return Object.entries(node as Record<string, unknown>).some(
      ([key, value]) => key !== 'kind' && key !== 'type' && hasText(value),
    )
  }
  return false
}

/**
 * **Why this section will not appear on the firm's site, or `null`.**
 *
 * `contact` is the one type that reads the site's own details rather than its
 * props, so a caller that has them passes them; a caller that does not (the
 * builder) passes nothing and gets the answer for a firm with no details,
 * which is the honest one for a section that draws them.
 */
export function whyHidden(
  type: string | undefined,
  props: Record<string, unknown>,
  ctx: BlockContext,
  facts: SiteFacts = {},
): HiddenReason | null {
  if (!type) return null

  // The conduct policy first, because it is the more useful of the two
  // reasons: the reader returns `null` for it as well, and "it has no
  // content" would be a true sentence about the wrong problem.
  const switchName = SUPPRESSED_BY[type]
  if (switchName && ctx.switches?.[switchName] !== true) {
    return {
      code: 'policy',
      message: `This section will not appear on your site: your profession’s rules do not let you ${
        SWITCH_WORDS[switchName] ?? switchName.replace(/_/g, ' ')
      }.`,
      switchName,
      settingsHref: CONDUCT_HREF,
    }
  }

  const empty = (needs = NEEDS[type]): HiddenReason => ({
    code: 'empty',
    message: needs
      ? `This section will not appear on your site until it has ${needs}.`
      : 'This section will not appear on your site: it has no content yet.',
  })

  if (type === 'rich_text' || type === 'image') {
    return rawIsEmpty(type, props) ? empty() : null
  }

  // ── the seven the service fills in at render ────────────────────────────
  if (RESOLVED_AT_RENDER.has(type)) {
    // The typed part, where it is decisive on its own.
    if (type === 'services' && choseNoServices(props)) {
      return { code: 'empty', message: NO_SERVICES_CHOSEN }
    }
    const slugs = NEEDS_SLUGS[type]
    if (slugs && !(Array.isArray(props.service_slugs) && props.service_slugs.length > 0)) {
      return empty(slugs)
    }
    if (type === 'services' && facts.services === 0) return empty('at least one service in your catalogue')
    if (type === 'blog_list' && facts.posts === 0) return empty('at least one published post')
    if (type === 'hours' && facts.hasHours === false) {
      return empty('opening and closing times on at least one day, under Your firm')
    }
    if (type === 'contact' && facts.contact) {
      // The only one a caller's facts settle outright: the block draws the
      // firm's details, and `readContact` is what decides.
      return blockView(type, props, ctx, facts.contact) === null ? empty() : null
    }
    // Anything else about these seven is the service's to know. Declining is
    // deliberate — see the file header on which direction of error matters.
    return null
  }

  // The renderer's own question. A type nothing knows returns `null` from
  // `blockView` and is reported as empty, which is what the page does with
  // it: an unknown type draws nothing (`blocks/index.tsx`'s header).
  const view = blockView(type, props, ctx, facts.contact ?? NO_CONTACT)
  return view === null || view === undefined ? empty() : null
}

/** Whether this section will appear — the same question, as a boolean. */
export function willDraw(
  type: string | undefined,
  props: Record<string, unknown>,
  ctx: BlockContext,
  facts: SiteFacts = {},
): boolean {
  return whyHidden(type, props, ctx, facts) === null
}
